import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import "./App.css";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Checkbox } from "./components/ui/checkbox";
import { Progress } from "./components/ui/progress";
import { Upload, FileText, Music, Image, ChevronLeft, ChevronRight, Plus, Trash2, Star, Globe, Palette, Sparkles, Settings, RotateCcw, Type, Check, ChevronsUpDown, Save, FolderOpen, Download } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./components/ui/command";
import WebPPTGenerator from "./WebPPTGenerator";
import mammoth from "mammoth";
import * as XLSX from "xlsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dernière fiche imprimée automatiquement (évite le double appel à window.print)
let autoPrintedSheetId = null;

// Liste des genres avec leurs couleurs officielles Kuroneko
const GENRES = [
  "BLUES", "SOUL / FUNK", "COUNTRY", "CLASSIQUE", "BO", "RELIGIEUX", 
  "RELAXATION", "JEUNESSE", "LIVRE AUDIO", "SPECTACLE", "ELECTRO", 
  "ROCK", "METAL", "POP", "VF", "INDIE", "HIP-HOP", "REGGAE", "WORLD"
];

// Couleurs officielles des genres musicaux Kuroneko
const GENRE_COLORS = {
  "BLUES": "#FF33CC",        // Rose
  "SOUL / FUNK": "#FF33CC",  // Rose
  "COUNTRY": "#FF33CC",      // Rose
  "CLASSIQUE": "#CC9900",    // Jaune
  "BO": "#9900CC",           // Violet
  "RELIGIEUX": "#9900CC",    // Violet
  "RELAXATION": "#9900CC",   // Violet
  "JEUNESSE": "#9900CC",     // Violet
  "LIVRE AUDIO": "#9900CC",  // Violet
  "SPECTACLE": "#9900CC",    // Violet
  "ELECTRO": "#0A29C2",      // Bleu
  "ROCK": "#FF0000",         // Rouge
  "METAL": "#FF0000",        // Rouge
  "POP": "#E97132",          // Orange
  "VF": "#E97132",           // Orange
  "INDIE": "#E97132",        // Orange
  "HIP-HOP": "#196B24",      // Vert
  "REGGAE": "#196B24",       // Vert
  "WORLD": "#196B24"         // Vert
};

const COLOR_THEMES = [
  { name: "Kuroneko Dark", primary: "#00ff41", secondary: "#39ff14", accent: "#ffffff" }, // Vert fluo principal
  { name: "Kuroneko Classic", primary: "#16a34a", secondary: "#dc2626", accent: "#f97316" },
  { name: "Ocean Blue", primary: "#0ea5e9", secondary: "#3b82f6", accent: "#8b5cf6" },
  { name: "Sunset Orange", primary: "#f97316", secondary: "#ef4444", accent: "#eab308" },
  { name: "Forest Green", primary: "#059669", secondary: "#16a34a", accent: "#65a30d" },
  { name: "Royal Purple", primary: "#7c3aed", secondary: "#a855f7", accent: "#ec4899" },
  { name: "Midnight Dark", primary: "#1f2937", secondary: "#374151", accent: "#6b7280" }
];

// Helper function to parse simple markdown (bold, italic, underline)
const parseMarkdown = (text) => {
  if (!text) return text;
  
  // Split by markdown patterns and create React elements
  const parts = [];
  let remaining = text;
  let key = 0;
  
  // Pattern for **bold**, *italic*, _underline_
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_)/g;
  let lastIndex = 0;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    
    // Add the formatted text
    if (match[2]) {
      // **bold**
      parts.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      // *italic*
      parts.push(<em key={key++}>{match[3]}</em>);
    } else if (match[4]) {
      // _underline_
      parts.push(<span key={key++} style={{ textDecoration: 'underline' }}>{match[4]}</span>);
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
};

function FormGenerator() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, isAdmin } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showWebGenerator, setShowWebGenerator] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [loadedSheetId, setLoadedSheetId] = useState(null);
  
  // Data from backend
  const [kurPrix, setKurPrix] = useState([]);
  const [codePays, setCodePays] = useState([]);
  const [mediaList, setMediaList] = useState([]);
  const [logosList, setLogosList] = useState([]);
  const [labelLogos, setLabelLogos] = useState([]);
  const [genresList, setGenresList] = useState([]);
  
  // Form data state
  const [formData, setFormData] = useState({
    // Step 1: Basic info
    artist: "",
    album: "",
    album_type: "",
    argumentaire: "",
    genre: "",
    sous_genre: "",
    selected_label: "",
    coup_de_coeur: false,
    sheet_language: "FR", // "FR" = modèle France, "EN" = modèle Export
    enable_clip: false, // Option pour activer/désactiver le clip
    enable_et_aussi: false, // Option pour activer/désactiver "Et Aussi"
    enable_marketing: false, // Option pour activer/désactiver marketing & réseaux
    enable_concerts: false, // Option pour activer/désactiver Concerts
    social_media_descriptions: {}, // Descriptions pour les réseaux sociaux
    
    // Step 2: Tracks and media
    tracks: [],
    ecouter_link: "",
    clip_link: "",
    clip_button_color: "", // Couleur du bouton clip : red, blue, green, purple, orange (vide = non sélectionné)
    
    // Step 3: Formats (dynamic)
    formats: [], // Array of format objects
    pc_type: "", // Type de PC ou Bon (vide = non sélectionné)
    pc_expres: "",
    fin_de_preco: "",
    date_de_sortie: "",
    
    // Step 4: Concerts
    concerts: [],
    concert_mode: "", // "manual" or "file"
    concert_all_dates: [], // Toutes les dates détectées dans le fichier importé
    concert_file_name: "", // Nom du fichier importé
    concert_overflow: false, // true si plus de 10 concerts détectés
    concert_free_text: "", // Free text that appears on the generated sheet
    concert_page_id: "", // ID of the public concert dates page
    
    // Step 5: Media and social
    selected_media: [],
    selected_logos: [],
    media_names: [],
    media_descriptions: {}, // New: store custom descriptions for each selected media
    social_media_links: {}, // New: store links for each selected social media
    et_aussi: "",
    et_aussi_medias: [], // New: "Et Aussi" media selection (logos only)
    
    // Kuroneko links
    kuroneko_website: "https://www.kuroneko-distribution.com",
    kuroneko_facebook: "https://www.facebook.com/kuronekomedia",
    kuroneko_instagram: "https://www.instagram.com/kuronekomedia",
    
    // Font size controls (facultatifs avec valeurs par défaut)
    font_size_tracklist: "", // Facultatif - vide = défaut
    font_size_argumentaire: "", // Facultatif - vide = défaut  
    font_size_catalog: "", // Facultatif - vide = défaut
    font_size_artist_title: "", // Facultatif - vide = défaut
    font_size_title: "", // Facultatif - vide = défaut (Titre album)
    font_size_promotion: "", // Facultatif - vide = défaut
    font_size_concert: "", // Facultatif - vide = défaut
    font_size_cdlp: "", // Facultatif - vide = défaut (pour les supports CD/LP)
    font_size_buttons: "", // Facultatif - vide = défaut (Boutons ECOUTER/CLIP)
    font_size_kuroneko_logo: "", // Facultatif - vide = défaut (Logo Kuroneko)
    font_size_genre: "", // Facultatif - vide = défaut (Genre/Sous-genre)
    font_size_marketing: "", // Facultatif - vide = défaut (Marketing & Réseaux)
    
    // Font family controls (facultatifs avec valeurs par défaut)
    font_family_tracklist: "", // Facultatif - vide = Arial
    font_family_argumentaire: "", // Facultatif - vide = Arial
    font_family_catalog: "", // Facultatif - vide = Arial
    font_family_artist_title: "", // Facultatif - vide = Arial
    font_family_promotion: "", // Facultatif - vide = Arial
    font_family_concert: "", // Facultatif - vide = Arial
    font_family_cdlp: "", // Facultatif - vide = Arial
    font_family_buttons: "", // Facultatif - vide = Arial
    font_family_kuroneko_logo: "", // Facultatif - vide = Arial
    font_family_genre: "", // Facultatif - vide = Arial
    font_family_marketing: "", // Facultatif - vide = Arial
    
    // Size controls for CD/LP elements
    size_exclu_disquaires: "", // Facultatif - vide = défaut (55-70px)
    size_barcode: "", // Facultatif - vide = défaut (60-80px)
    size_label_logo: "", // Facultatif - vide = défaut (132px de large)
    
    // Tracklist width control
    tracklist_width: "", // Facultatif - vide = 30% (tracklist) / 70% (promotion)
    
    // Couleurs personnalisables pour les dates (Étape 6)
    date_preco_color: "", // Couleur pour date de précommande (vide = couleur par défaut)
    date_sortie_color: "", // Couleur pour date de sortie (vide = couleur par défaut)
    clip_color: "" // Couleur du bouton Clip officiel (vide = couleur par défaut)
  });
  
  // Image files state
  const [images, setImages] = useState({
    cover_image: null,
    clip_image: null
  });

  // Load data from backend on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [prixResponse, paysResponse, mediaResponse, socialResponse] = await Promise.all([
          fetch(`${API}/kur-prix`),
          fetch(`${API}/code-pays`),
          fetch(`${API}/media-list`),
          fetch(`${API}/data/social-networks`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          })
        ]);
        
        if (prixResponse.ok) {
          const prixData = await prixResponse.json();
          setKurPrix(prixData.prix);
        }
        
        if (paysResponse.ok) {
          const paysData = await paysResponse.json();
          setCodePays(paysData.countries);
        }
        
        if (mediaResponse.ok) {
          const mediaData = await mediaResponse.json();
          setMediaList(mediaData.media);
          console.log("Media data loaded:", mediaData.media); // Debug log
        }
        
        if (socialResponse.ok) {
          const socialData = await socialResponse.json();
          setLogosList(socialData.social_networks.map(sn => ({
            name: sn.name,
            filename: sn.filename
          })));
          console.log("Social networks loaded:", socialData.social_networks);
        }

        // Load label logos
        const labelResponse = await fetch(`${API}/data/label-logos`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (labelResponse.ok) {
          const labelData = await labelResponse.json();
          setLabelLogos(labelData.label_logos || []);
        }

        // Load genres
        const genresResponse = await fetch(`${API}/data/genres`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (genresResponse.ok) {
          const genresData = await genresResponse.json();
          setGenresList(genresData.genres || []);
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Erreur lors du chargement des données");
      }
    };
    
    loadData();
  }, []);

  // Load saved sheet from URL param
  useEffect(() => {
    const sheetId = searchParams.get('load');
    if (sheetId) {
      const autoPrint = searchParams.get('print') === '1';
      loadSavedSheet(sheetId, autoPrint);
      // Clean the URL
      setSearchParams({});
    }
  }, [searchParams]);

  const loadSavedSheet = async (sheetId, autoPrint = false) => {
    try {
      const response = await fetch(`${API}/sheets/${sheetId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) {
        toast.error("Fiche introuvable");
        return;
      }
      const data = await response.json();
      const sheet = data.sheet;
      
      // Load form data (merge avec les valeurs par défaut pour les anciens schémas)
      setFormData(prev => ({ ...prev, ...sheet.form_data }));
      setLoadedSheetId(sheetId);
      
      // Load images if they exist
      const newImages = { cover_image: null, clip_image: null };
      if (sheet.cover_image) {
        try {
          const imgResponse = await fetch(`${API}/sheet-images/${sheet.cover_image}`);
          if (imgResponse.ok) {
            const blob = await imgResponse.blob();
            newImages.cover_image = new File([blob], sheet.cover_image, { type: blob.type });
          }
        } catch (e) { console.error("Error loading cover image:", e); }
      }
      if (sheet.clip_image) {
        try {
          const imgResponse = await fetch(`${API}/sheet-images/${sheet.clip_image}`);
          if (imgResponse.ok) {
            const blob = await imgResponse.blob();
            newImages.clip_image = new File([blob], sheet.clip_image, { type: blob.type });
          }
        } catch (e) { console.error("Error loading clip image:", e); }
      }
      setImages(newImages);
      
      if (autoPrint) {
        // Téléchargement PDF direct depuis la liste des fiches enregistrées :
        // aucun toast, il se retrouverait dans le PDF
        setShowWebGenerator(true);
        // Verrou module : React.StrictMode remonte le composant, un useRef serait recréé.
        // Il est relâché au déclenchement pour qu'un second téléchargement reste possible.
        if (autoPrintedSheetId !== sheetId) {
          autoPrintedSheetId = sheetId;
          setTimeout(() => { autoPrintedSheetId = null; toast.dismiss(); window.print(); }, 2500);
        }
      } else {
        setCurrentStep(1);
        toast.success(`Fiche "${sheet.name}" chargée`);
      }
    } catch (error) {
      console.error("Error loading sheet:", error);
      toast.error("Erreur lors du chargement de la fiche");
    }
  };

  const saveSheet = async () => {
    if (!saveName.trim()) {
      toast.error("Veuillez saisir un nom pour la fiche");
      return;
    }
    if (!formData.pc_type) {
      toast.error("Veuillez sélectionner un type de PC ou Bon (Étape 3)");
      return;
    }
    
    setIsSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', saveName.trim());
      fd.append('category', formData.pc_type);
      fd.append('form_data', JSON.stringify(formData));
      if (images.cover_image) fd.append('cover_image', images.cover_image);
      if (images.clip_image) fd.append('clip_image', images.clip_image);
      
      const response = await fetch(`${API}/sheets`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: fd
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success("Fiche enregistrée avec succès !");
        setShowSaveDialog(false);
        setLoadedSheetId(result.sheet_id);
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (error) {
      console.error("Error saving sheet:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setIsSaving(false);
    }
  };

  const openSaveDialog = () => {
    const defaultName = `${formData.artist || "Artiste"} - ${formData.album || "Titre"} - ${formData.pc_type || "PC"} ${formData.pc_expres || ""}`.trim();
    setSaveName(defaultName);
    setShowSaveDialog(true);
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Auto-fill function for testing
  const autoFillForm = () => {
    // Wait for data to be loaded
    if (mediaList.length === 0 || kurPrix.length === 0 || codePays.length === 0) {
      toast.error("Attendez que les données soient chargées", {
        description: "Les données du backend sont en cours de chargement..."
      });
      return;
    }
    
    const testData = {
      // Step 1: Basic info
      artist: "LEONARD LASRY",
      album: "Le grand danger de se plaire",
      album_type: "Album",
      argumentaire: "Pour *ce nouveau chapitre musical*, le compositeur et interprète se libère d'un format jusqu'alors majoritairement ancré dans la chanson française plus traditionnelle. Riche de ses multiples collaborations, **Léonard Lasry** livre un album musicalement au carrefour de tout ce qui constitue son amour pour la chanson et la musique pop au sens le plus large du terme, particulièrement nourri de ses récentes réalisations lesquelles avaient parfois exploré des courants musicaux jusqu'alors inédits à son propre répertoire.\nSi l'on savoure les climats teintés de trip hop sur Le grand danger de se plaire ou \"Avec mes yeux d'océan\", on retrouve des accents quelque peu dancefloor sur Pas la même mémoire ou encore l'intimité d'un piano sur En passant par l'amour. Enfin, on savoure Un autre jour dans la nuit un envoutant slow chanté en duo avec **Fanny Ardant**.\nAprès les très réussis Avant la première fois (2017) et Au hasard cet espoir (2021), Léonard Lasry nous offre ici un troisième album.",
      genre: "POP",
      sous_genre: "Pop Française",
      selected_label: labelLogos.length > 0 ? labelLogos[0].name : "29Music",
      coup_de_coeur: true,
      enable_clip: true,
      enable_et_aussi: true,
      enable_marketing: true,
      enable_concerts: true,
      
      // Step 2: Tracks and media
      tracks: [
        "Intro automatique",
        "Test de chanson",
        "Formulaire en folie",
        "Debug session",
        "Code complet",
        "Validation réussie",
        "Production finale",
        "Outro test",
        "Chanson neuf",
        "Dixième titre",
        "Onzième morceau",
        "Douzième piste",
        "Treizième titre",
        "Quatorzième chanson",
        "Quinzième morceau",
        "Seizième piste",
        "Dix-septième titre",
        "Dix-huitième chanson",
        "Dix-neuvième morceau",
        "Vingtième et dernier",
        "Vingt-et-unième bonus"
      ],
      ecouter_link: "https://open.spotify.com/album/test-auto",
      clip_link: "https://www.youtube.com/watch?v=test-auto",
      clip_button_color: "",
      
      // Step 3: Formats - 3 FORMATS
      formats: [
        {
          id: Date.now(),
          type: "CD",
          text: "Digipack",
          release_date: "2025-01-15",
          preorder_end_date: "2025-01-10",
          num_catalog: "29MU0042CD",
          prix: kurPrix[0]?.code || "KUR01",
          exclu_disc: "",
          exclu_disquaires: true,
          code_barre_image: null
        },
        {
          id: Date.now() + 1,
          type: "CD",
          text: "Jewel Case",
          release_date: "2025-01-15",
          preorder_end_date: "2025-01-10",
          num_catalog: "29MU0042CD",
          prix: kurPrix[2] ? `${kurPrix[2].code}-${kurPrix[2].price}` : "KUR03-3,51 €",
          exclu_disc: "",
          exclu_disquaires: false,
          code_barre_image: null
        },
        {
          id: Date.now() + 2,
          type: "LP",
          text: "Vinyle 180g",
          release_date: "2025-01-15",
          preorder_end_date: "",
          num_catalog: "29MU0042CD",
          prix: kurPrix[5] ? `${kurPrix[5].code}-${kurPrix[5].price}` : "KUR06-4,57 €",
          exclu_disc: "",
          exclu_disquaires: false,
          code_barre_image: null
        }
      ],
      pc_type: "PC EXPRESS N°",
      pc_expres: "2025",
      fin_de_preco: "2025-01-10",
      date_de_sortie: "2025-01-15",
      
      // Step 4: Concerts
      concert_mode: "manual",
      concert_all_dates: [],
      concert_file_name: "",
      concert_overflow: false,
      concert_free_text: "",
      concerts: [
        {
          id: Date.now(),
          date: "2025-02-01",
          text: "Le Trianon, Paris",
          country_code: codePays[0]?.code || "FR"
        },
        {
          id: Date.now() + 1,
          date: "2025-02-05",
          text: "L'Olympia, Paris",
          country_code: codePays[0]?.code || "FR"
        },
        {
          id: Date.now() + 2,
          date: "2025-02-10",
          text: "Ancienne Belgique, Bruxelles",
          country_code: codePays[1]?.code || "BE"
        }
      ],
      
      // Step 5: Media and social
      selected_media: ["Le Figaro", "France Inter", "France Bleu", "Télérama", "Rock & Folk"],
      selected_logos: [],
      media_names: [],
      media_descriptions: {
        "Le Figaro": "Critique et itw le 10/02/24",
        "France Inter": "Emission « La bande originale » le 02/03/24",
        "France Bleu": "2 passages / jour de « Why » en mars 2024",
        "Télérama": "« Un disque qui vous accroche un peu plus au fur et à mesure des écoutes, pour ne plus vous lâcher »",
        "Rock & Folk": "« L'ancien complice d'Étienne Daho publie son sixième album solo, \"Sur la photo\", nouveau délice de pop mélancolique »"
      },
      social_media_links: {
        "Facebook": "https://facebook.com/leonardlasry",
        "Twitter": "https://twitter.com/leonardlasry"
      },
      social_media_descriptions: {
        "Facebook": "Campagne clip « Why » (5K de budget) du 09 au 23/02/24\n10K abonnés",
        "Twitter": "Campagne clip « Why » (5K de budget) du 09 au 23/02/24\n10K abonnés"
      },
      et_aussi: "",
      et_aussi_medias: mediaList.slice(3, 5).map(m => m.name),
      
      // Kuroneko links
      kuroneko_website: "https://www.kuroneko-distribution.com",
      kuroneko_facebook: "https://www.facebook.com/kuronekomedia",
      kuroneko_instagram: "https://www.instagram.com/kuronekomedia",
      
      // Font sizes (default)
      font_size_tracklist: "",
      font_size_argumentaire: "",
      font_size_catalog: "",
      font_size_artist_title: "",
      font_size_promotion: "",
      font_size_concert: ""
    };
    
    // Add media descriptions for selected media
    mediaList.slice(0, 3).forEach((media, index) => {
      testData.media_descriptions[media.name] = `${media.name} - Test description ${index + 1}`;
    });
    
    setFormData(prev => ({ ...prev, ...testData }));
    toast.success("Formulaire rempli automatiquement !", {
      description: "Toutes les données de test ont été chargées"
    });
  };

  const handleImageUpload = (field, file) => {
    setImages(prev => ({ ...prev, [field]: file }));
    toast.success(`Image ${field} téléchargée avec succès`);
  };

  const handleTracklistUpload = async (file) => {
    try {
      const fileName = file.name.toLowerCase();
      
      // Handle Word files (.docx) locally with mammoth
      if (fileName.endsWith('.docx')) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        const text = result.value;
        
        // Split by newlines and filter empty lines
        const tracks = text.split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);
        
        if (tracks.length > 0) {
          updateFormData('tracks', tracks);
          toast.success(`${tracks.length} pistes importées depuis Word`);
        } else {
          toast.error("Aucune piste trouvée dans le fichier Word");
        }
        return;
      }
      
      // Handle text files via backend
      if (fileName.endsWith('.txt')) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await fetch(`${API}/parse-tracklist`, {
          method: 'POST',
          body: formDataUpload
        });
        
        if (response.ok) {
          const result = await response.json();
          updateFormData('tracks', result.tracks);
          toast.success(`${result.tracks.length} pistes importées`);
        } else {
          toast.error("Erreur lors de l'import de la tracklist");
        }
        return;
      }
      
      toast.error("Format non supporté. Utilisez .txt ou .docx");
    } catch (error) {
      console.error("Error uploading tracklist:", error);
      toast.error("Erreur lors de l'import de la tracklist");
    }
  };

  // Format management
  const addFormat = () => {
    const newFormat = {
      id: Date.now(),
      type: "",
      text: "",
      release_date: "",
      preorder_end_date: "",
      num_catalog: "",
      prix: "",
      exclu_disc: "",
      exclu_disquaires: false, // New field
      code_barre_image: null
    };
    updateFormData('formats', [...formData.formats, newFormat]);
  };

  const updateFormat = (id, field, value) => {
    const newFormats = formData.formats.map(format => 
      format.id === id ? { ...format, [field]: value } : format
    );
    updateFormData('formats', newFormats);
  };

  const removeFormat = (id) => {
    const newFormats = formData.formats.filter(format => format.id !== id);
    updateFormData('formats', newFormats);
  };

  const handleFormatImageUpload = (id, file) => {
    const newFormats = formData.formats.map(format => 
      format.id === id ? { ...format, code_barre_image: file } : format
    );
    updateFormData('formats', newFormats);
    toast.success("Code-barre téléchargé avec succès");
  };

  // Concert management
  const addConcert = () => {
    const max = formData.concert_overflow ? 2 : 10;
    if (formData.concerts.length < max) {
      updateFormData('concerts', [...formData.concerts, { id: Date.now(), date: "", text: "", country_code: "" }]);
    }
  };

  const updateConcert = (id, field, value) => {
    const newConcerts = formData.concerts.map(concert => 
      concert.id === id ? { ...concert, [field]: value } : concert
    );
    updateFormData('concerts', newConcerts);
  };

  const removeConcert = (id) => {
    const newConcerts = formData.concerts.filter(concert => concert.id !== id);
    updateFormData('concerts', newConcerts);
  };

  // ===== Concert file parsing (Word/Excel) =====
  // Convertit une valeur (Date, serial Excel formaté, "JJ/MM/AAAA") en ISO YYYY-MM-DD
  const toISODate = (value) => {
    if (value instanceof Date && !isNaN(value.getTime())) {
      return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    }
    const s = String(value ?? '').replace(/[\u00A0\u202F]/g, ' ').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
    if (m) {
      let year = parseInt(m[3], 10);
      if (year < 100) year += 2000;
      return `${year}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    }
    return "";
  };

  // "Le Trianon, Paris, FR" -> { text: "Le Trianon, Paris", country_code: "FR" }
  const splitVenue = (parts) => {
    const clean = parts.map(p => String(p ?? '').replace(/[\u00A0\u202F]/g, ' ').trim()).filter(p => p.length > 0);
    let country_code = "";
    if (clean.length > 1 && clean[clean.length - 1].length <= 3) {
      country_code = clean.pop().toUpperCase();
    }
    return { text: clean.join(', '), country_code };
  };

  const parseConcertLine = (rawLine) => {
    // Word insère des espaces insécables (U+00A0) et parfois des tirets longs
    const line = rawLine.replace(/[\u00A0\u202F]/g, ' ').replace(/[\u2013\u2014]/g, '-').trim();
    const sep = line.indexOf(' - ');
    if (sep === -1) return null;
    const date = toISODate(line.slice(0, sep));
    if (!date) return null;
    const { text, country_code } = splitVenue(line.slice(sep + 3).split(','));
    if (!text) return null;
    return { date, text, country_code };
  };

  const applyParsedConcerts = (parsed, fileName) => {
    if (parsed.length === 0) {
      toast.error("Aucune date de concert détectée", {
        description: "Format attendu : JJ/MM/AAAA - Lieu, Ville, Code pays"
      });
      return;
    }
    const withIds = parsed.map((c, i) => ({ ...c, id: Date.now() + i }));
    updateFormData('concert_all_dates', withIds);
    updateFormData('concert_file_name', fileName);

    if (withIds.length > 10) {
      updateFormData('concert_overflow', true);
      updateFormData('concerts', withIds.slice(0, 2));
      toast.success(`${withIds.length} concerts détectés`, {
        description: "Plus de 10 dates : résumez la tournée et publiez la page des concerts"
      });
    } else {
      updateFormData('concert_overflow', false);
      updateFormData('concerts', withIds);
      toast.success(`${withIds.length} concerts importés depuis "${fileName}"`);
    }
  };

  const handleConcertFileUpload = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();

    try {
      if (ext === 'docx') {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        const parsed = result.value
          .split('\n')
          .map(l => l.trim())
          .filter(l => l.length > 0)
          .map(parseConcertLine)
          .filter(Boolean);
        applyParsedConcerts(parsed, file.name);
      } else if (ext === 'csv') {
        // CSV : on lit le texte brut (SheetJS interprète 01/03/2025 à l'américaine)
        const text = await file.text();
        const parsed = text
          .split(/\r?\n/)
          .map(line => {
            const cols = line.split(/[;,\t]/).map(c => c.trim().replace(/^"|"$/g, ''));
            const date = toISODate(cols[0]);
            if (!date) return null;
            const { text: venue, country_code } = splitVenue(cols.slice(1));
            if (!venue) return null;
            return { date, text: venue, country_code };
          })
          .filter(Boolean);
        applyParsedConcerts(parsed, file.name);
      } else if (ext === 'xlsx' || ext === 'xls') {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { cellDates: true });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, blankrows: false });
        const parsed = rows
          .map(row => {
            const date = toISODate(row[0]);
            if (!date) return null;
            const { text, country_code } = splitVenue(row.slice(1));
            if (!text) return null;
            return { date, text, country_code };
          })
          .filter(Boolean);
        applyParsedConcerts(parsed, file.name);
      } else {
        toast.error("Format non supporté. Utilisez .docx, .xlsx ou .csv");
      }
    } catch (error) {
      console.error("Error parsing concert file:", error);
      toast.error("Erreur lors de la lecture du fichier");
    }
  };

  // Ligne finale : JJ/MM/AA - Lieu, Ville, Code pays
  const formatConcertLine = (concert) => {
    let shortDate = concert.date;
    const iso = concert.date && concert.date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) shortDate = `${iso[3]}/${iso[2]}/${iso[1].slice(2)}`;
    return `${shortDate} - ${concert.text}${concert.country_code ? `, ${concert.country_code}` : ''}`;
  };

  const publishConcertPage = async () => {
    const allDates = formData.concert_all_dates || [];
    if (allDates.length === 0) {
      toast.error("Aucune date de concert importée");
      return;
    }
    try {
      const body = {
        artist: formData.artist || "",
        album: formData.album || "",
        concert_text: allDates.map(formatConcertLine).join('\n'),
        free_text: formData.concert_free_text || ""
      };
      
      let pageId = formData.concert_page_id;
      
      if (pageId) {
        // Update existing page
        await fetch(`${API}/concert-pages/${pageId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      } else {
        // Create new page
        const response = await fetch(`${API}/concert-pages`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (response.ok) {
          const data = await response.json();
          pageId = data.page_id;
          updateFormData('concert_page_id', pageId);
        }
      }
      
      toast.success("Page de concerts publiée !");
      return pageId;
    } catch (error) {
      console.error("Error publishing concert page:", error);
      toast.error("Erreur lors de la publication");
    }
  };

  // Media names management
  const addMediaName = () => {
    if (formData.media_names.length < 9) {
      updateFormData('media_names', [...formData.media_names, ""]);
    }
  };

  const updateMediaName = (index, value) => {
    const newNames = [...formData.media_names];
    newNames[index] = value;
    updateFormData('media_names', newNames);
  };

  const removeMediaName = (index) => {
    const newNames = formData.media_names.filter((_, i) => i !== index);
    updateFormData('media_names', newNames);
  };

  // Media selection management
  const toggleMediaSelection = (mediaName) => {
    const isSelected = formData.selected_media.includes(mediaName);
    if (isSelected) {
      updateFormData('selected_media', formData.selected_media.filter(m => m !== mediaName));
      // Remove description when media is deselected
      const newDescriptions = { ...formData.media_descriptions };
      delete newDescriptions[mediaName];
      updateFormData('media_descriptions', newDescriptions);
    } else {
      updateFormData('selected_media', [...formData.selected_media, mediaName]);
    }
  };

  // Update media description
  const updateMediaDescription = (mediaName, description) => {
    const newDescriptions = { ...formData.media_descriptions };
    newDescriptions[mediaName] = description;
    updateFormData('media_descriptions', newDescriptions);
  };

  // Logo selection management
  const toggleLogoSelection = (logoName) => {
    const isSelected = formData.selected_logos.includes(logoName);
    if (isSelected) {
      updateFormData('selected_logos', formData.selected_logos.filter(l => l !== logoName));
      // Remove social media link when logo is deselected
      const newLinks = { ...formData.social_media_links };
      delete newLinks[logoName];
      updateFormData('social_media_links', newLinks);
    } else {
      updateFormData('selected_logos', [...formData.selected_logos, logoName]);
    }
  };

  // Update social media link
  const updateSocialMediaLink = (logoName, link) => {
    const newLinks = { ...formData.social_media_links };
    newLinks[logoName] = link;
    updateFormData('social_media_links', newLinks);
  };

  // Et Aussi media selection management  
  const toggleEtAussiMedia = (mediaName) => {
    const isSelected = formData.et_aussi_medias.includes(mediaName);
    if (isSelected) {
      updateFormData('et_aussi_medias', formData.et_aussi_medias.filter(m => m !== mediaName));
    } else {
      updateFormData('et_aussi_medias', [...formData.et_aussi_medias, mediaName]);
    }
  };

  const generateWebVersion = () => {
    if (!formData.artist || !formData.album || !formData.genre) {
      toast.error("Veuillez remplir au minimum l'artiste, l'album et le genre");
      return;
    }
    
    setShowWebGenerator(true);
    toast.success("Fiche promotionnelle générée ! Vous pouvez maintenant l'éditer et l'exporter.");
  };

  const nextStep = () => {
    if (currentStep < 6) {
      let nextStepNum = currentStep + 1;
      // Skip step 4 (Concerts) if not enabled
      if (nextStepNum === 4 && !formData.enable_concerts) {
        nextStepNum = 5;
      }
      setCurrentStep(nextStepNum);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      let prevStepNum = currentStep - 1;
      // Skip step 4 (Concerts) if not enabled
      if (prevStepNum === 4 && !formData.enable_concerts) {
        prevStepNum = 3;
      }
      setCurrentStep(prevStepNum);
    }
  };

  const ImageUploadCard = ({ field, title, accept = "image/*" }) => (
    <Card className="border-2 border-dashed border-blue-200 hover:border-blue-400 transition-all duration-300 bg-gradient-to-br from-blue-50 to-indigo-50">
      <CardContent className="p-6 text-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="p-3 bg-blue-100 rounded-full">
            <Image className="h-8 w-8 text-blue-600" />
          </div>
          <Label className="text-sm font-medium text-blue-700">
            {title}
          </Label>
          <Button
            type="button"
            onClick={() => document.getElementById(field).click()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            📥 Importer
          </Button>
          <Input
            id={field}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) handleImageUpload(field, file);
            }}
          />
          {images[field] && (
            <span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-1 rounded-full">
              ✓ {images[field].name}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderSaveDialog = () => (
    showSaveDialog ? (
      <div className="no-print fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowSaveDialog(false)}>
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-lg mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-xl font-bold text-lime-400 mb-2">Enregistrer la fiche</h3>
          <p className="text-gray-400 text-sm mb-4">
            Catégorie : <span className="text-white font-medium">{formData.pc_type || "Non défini"}</span>
          </p>
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-300">Nom de la fiche</label>
            <Input
              data-testid="save-sheet-name-input"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="Nom de la fiche..."
              className="h-12 bg-gray-800 border-gray-600 text-white focus:border-lime-400"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') saveSheet(); }}
            />
          </div>
          <div className="flex gap-3 mt-6 justify-end">
            <Button variant="outline" onClick={() => setShowSaveDialog(false)} className="border-gray-600 text-gray-300 hover:bg-gray-800">
              Annuler
            </Button>
            <Button 
              data-testid="confirm-save-sheet-btn"
              onClick={saveSheet} 
              disabled={isSaving || !saveName.trim()}
              className="bg-lime-400 text-black hover:bg-lime-500"
            >
              {isSaving ? "Enregistrement..." : "Enregistrer"}
            </Button>
            <Button
              data-testid="download-pdf-btn"
              onClick={() => { setShowSaveDialog(false); toast.dismiss(); setTimeout(() => window.print(), 300); }}
              className="gap-2 bg-blue-600 text-white hover:bg-blue-700"
            >
              <Download className="h-4 w-4" />
              Télécharger en PDF
            </Button>
          </div>
        </div>
      </div>
    ) : null
  );

  const renderStep1 = () => (
    <div className="space-y-8">
    <div className="flex items-center justify-between gap-4 p-4 rounded-xl border-2 border-lime-500/40 bg-gradient-to-r from-gray-900 to-gray-800">
      <div>
        <p className="text-lg font-bold text-lime-400">Modèle de fiche</p>
        <p className="text-sm text-gray-400">
          {formData.sheet_language === 'EN'
            ? "Modèle Export : pas de code prix, genre en anglais, logo Staff picks"
            : "Modèle France : code prix, genre en français, logo Coup de cœur"}
        </p>
      </div>
      <div className="flex rounded-full bg-gray-700 p-1">
        <button
          type="button"
          data-testid="sheet-lang-fr"
          onClick={() => updateFormData('sheet_language', 'FR')}
          className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${formData.sheet_language !== 'EN' ? 'bg-lime-400 text-black shadow-lg' : 'text-gray-300 hover:text-white'}`}
        >
          FR
        </button>
        <button
          type="button"
          data-testid="sheet-lang-en"
          onClick={() => updateFormData('sheet_language', 'EN')}
          className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${formData.sheet_language === 'EN' ? 'bg-lime-400 text-black shadow-lg' : 'text-gray-300 hover:text-white'}`}
        >
          EN
        </button>
      </div>
    </div>

      {/* Artist & Album */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <Label htmlFor="artist" className="text-lg font-bold text-lime-400 drop-shadow-sm">Nom artiste</Label>
          <Input
            id="artist"
            value={formData.artist}
            onChange={(e) => updateFormData('artist', e.target.value.toUpperCase())}
            placeholder="Nom de l'artiste"
            className="h-12 text-lg border-2 focus:border-lime-400 bg-gray-800 text-white placeholder-gray-500 border-gray-600 shadow-lg focus:shadow-lime-400/20"
          />
        </div>
        <div className="space-y-3">
          <Label htmlFor="album" className="text-lg font-bold text-lime-400 drop-shadow-sm">Nom sortie</Label>
          <Input
            id="album"
            value={formData.album}
            onChange={(e) => updateFormData('album', e.target.value)}
            placeholder="Titre de la sortie"
            className="h-12 text-lg border-2 focus:border-lime-400 bg-gray-800 text-white placeholder-gray-500 border-gray-600 shadow-lg focus:shadow-lime-400/20"
          />
        </div>
      </div>
      
      {/* Description */}
      <div className="space-y-3">
        <Label htmlFor="argumentaire" className="text-lg font-bold text-lime-400 drop-shadow-sm">Argumentaire (max. 1000 caractères, espaces compris)</Label>
        <Textarea
          id="argumentaire"
          value={formData.argumentaire}
          onChange={(e) => {
            if (e.target.value.length <= 1000) {
              updateFormData('argumentaire', e.target.value);
            }
          }}
          placeholder="Description de la sortie, style musical, influences..."
          rows={5}
          maxLength={1000}
          className="border-2 focus:border-lime-400 text-base bg-gray-800 text-white placeholder-gray-500 border-gray-600 shadow-lg focus:shadow-lime-400/20"
        />
        <p className="text-xs text-gray-400 text-right">{formData.argumentaire.length}/1000 caractères</p>
        {/* Aide Markdown */}
        <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
          <p className="text-sm text-lime-300 font-semibold mb-2">Mise en forme du texte :</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <code className="bg-gray-800 px-2 py-1 rounded text-gray-300">**texte**</code>
              <span className="text-gray-400">→</span>
              <span className="text-white font-bold">Gras</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="bg-gray-800 px-2 py-1 rounded text-gray-300">*texte*</code>
              <span className="text-gray-400">→</span>
              <span className="text-white italic">Italique</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="bg-gray-800 px-2 py-1 rounded text-gray-300">_texte_</code>
              <span className="text-gray-400">→</span>
              <span className="text-white underline">Souligné</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Genre & Sous-genre - Ligne 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <Label htmlFor="genre" className="text-lg font-bold text-lime-400 drop-shadow-sm">{formData.sheet_language === 'EN' ? 'Music genre' : 'Genre'}</Label>
          <Select onValueChange={(value) => updateFormData('genre', value)} value={formData.genre}>
            <SelectTrigger data-testid="genre-select" className="h-12 border-2 bg-gray-800 text-white border-gray-600 focus:border-lime-400 shadow-lg">
              <SelectValue placeholder="Sélectionner un genre">
                {formData.genre && (
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded-sm border border-white/30" 
                      style={{ backgroundColor: (genresList.find(g => g.name === formData.genre) || {}).color || GENRE_COLORS[formData.genre] || '#666' }}
                    />
                    <span>{formData.sheet_language === 'EN' ? ((genresList.find(g => g.name === formData.genre) || {}).name_en || formData.genre) : formData.genre}</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-gray-800 text-white border-gray-600 max-h-80">
              {(genresList.length > 0 ? genresList : GENRES.map(g => ({ name: g, color: GENRE_COLORS[g] || '#666' }))).map(genre => (
                  <SelectItem 
                    key={genre.name} 
                    value={genre.name} 
                    className="hover:bg-gray-700 focus:bg-gray-600 cursor-pointer py-2"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-5 h-5 rounded-sm border border-white/30 flex-shrink-0" 
                        style={{ backgroundColor: genre.color }}
                      />
                      <span className="font-medium">{formData.sheet_language === 'EN' ? (genre.name_en || genre.name) : genre.name}</span>
                    </div>
                  </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          <Label htmlFor="sous_genre" className="text-lg font-bold text-lime-400 drop-shadow-sm">Sous-genre</Label>
          <Input
            id="sous_genre"
            value={formData.sous_genre}
            onChange={(e) => updateFormData('sous_genre', e.target.value)}
            placeholder="Préciser"
            className="h-12 border-2 focus:border-lime-400 bg-gray-800 text-white placeholder-gray-500 border-gray-600 shadow-lg focus:shadow-lime-400/20"
          />
        </div>
      </div>
      
      {/* Format de sortie & Label - Ligne 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <Label htmlFor="album_type" className="text-lg font-bold text-lime-400 drop-shadow-sm">Format de sortie</Label>
          <Select value={formData.album_type} onValueChange={(value) => updateFormData('album_type', value)}>
            <SelectTrigger className="h-12 border-2 bg-gray-800 text-white border-gray-600 focus:border-lime-400 shadow-lg">
              <SelectValue placeholder="Sélectionner un format" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 text-white border-gray-600">
              <SelectItem value="Single" className="hover:bg-gray-700 focus:bg-lime-400 focus:text-black">Single</SelectItem>
              <SelectItem value="EP" className="hover:bg-gray-700 focus:bg-lime-400 focus:text-black">EP</SelectItem>
              <SelectItem value="Mini-Album" className="hover:bg-gray-700 focus:bg-lime-400 focus:text-black">Mini-Album</SelectItem>
              <SelectItem value="Album" className="hover:bg-gray-700 focus:bg-lime-400 focus:text-black">Album</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          <Label className="text-lg font-bold text-lime-400 drop-shadow-sm">Logo label</Label>
          <Select value={formData.selected_label} onValueChange={(value) => updateFormData('selected_label', value)}>
            <SelectTrigger data-testid="label-logo-select" className="h-12 border-2 bg-gray-800 text-white border-gray-600 focus:border-lime-400 shadow-lg">
              <SelectValue placeholder="Sélectionner">
                {formData.selected_label && (
                  <div className="flex items-center gap-2">
                    {labelLogos.find(l => l.name === formData.selected_label)?.filename && (
                      <img src={`${API}/label-logos/${labelLogos.find(l => l.name === formData.selected_label).filename}`} alt="" className="w-5 h-5 object-contain" />
                    )}
                    <span>{formData.selected_label}</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-gray-800 text-white border-gray-600">
              {labelLogos.map((label) => (
                <SelectItem key={label.name} value={label.name} className="hover:bg-gray-700 focus:bg-lime-400 focus:text-black">
                  <div className="flex items-center gap-2">
                    <img src={`${API}/label-logos/${label.filename}`} alt={label.name} className="w-5 h-5 object-contain" />
                    <span>{label.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Options */}
      <Card className="bg-gradient-to-r from-green-800 to-lime-800 border-lime-500 shadow-xl">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-lime-300 mb-4">Activer / Désactiver les sections suivantes</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="coup_de_coeur"
                checked={formData.coup_de_coeur}
                onCheckedChange={(checked) => updateFormData('coup_de_coeur', checked)}
                className="w-5 h-5"
              />
              <Label htmlFor="coup_de_coeur" className="text-lg font-bold text-lime-300 flex items-center cursor-pointer drop-shadow-sm">
                <Star className="h-5 w-5 mr-2 text-yellow-300 drop-shadow-sm" />
                {formData.sheet_language === 'EN' ? 'Staff picks Kuroneko' : 'Coup de cœur Kuroneko'}
              </Label>
            </div>
            <div className="flex items-center space-x-3">
              <Checkbox
                id="enable_clip"
                checked={formData.enable_clip}
                onCheckedChange={(checked) => updateFormData('enable_clip', checked)}
                className="w-5 h-5"
              />
              <Label htmlFor="enable_clip" className="text-lg font-bold text-lime-300 flex items-center cursor-pointer drop-shadow-sm">
                🎬 Clip
              </Label>
            </div>
            <div className="flex items-center space-x-3">
              <Checkbox
                id="enable_et_aussi"
                checked={formData.enable_et_aussi}
                onCheckedChange={(checked) => updateFormData('enable_et_aussi', checked)}
                className="w-5 h-5"
              />
              <Label htmlFor="enable_et_aussi" className="text-lg font-bold text-lime-300 flex items-center cursor-pointer drop-shadow-sm">
                📌 "Et aussi" (Promotion)
              </Label>
            </div>
            <div className="flex items-center space-x-3">
              <Checkbox
                id="enable_marketing"
                checked={formData.enable_marketing}
                onCheckedChange={(checked) => updateFormData('enable_marketing', checked)}
                className="w-5 h-5"
              />
              <Label htmlFor="enable_marketing" className="text-lg font-bold text-lime-300 flex items-center cursor-pointer drop-shadow-sm">
                📱 Marketing et réseaux
              </Label>
            </div>
            <div className="flex items-center space-x-3">
              <Checkbox
                id="enable_concerts"
                checked={formData.enable_concerts}
                onCheckedChange={(checked) => updateFormData('enable_concerts', checked)}
                className="w-5 h-5"
              />
              <Label htmlFor="enable_concerts" className="text-lg font-bold text-lime-300 flex items-center cursor-pointer drop-shadow-sm">
                🎤 En concert
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderStep2 = () => {
    // Couleurs pour le bouton clip avec leurs codes hex
    const CLIP_BUTTON_COLORS = {
      red: { name: 'Rouge', hex: '#FF0000' },
      blue: { name: 'Bleu', hex: '#0A29C2' },
      green: { name: 'Vert', hex: '#196B24' },
      purple: { name: 'Violet', hex: '#9900CC' },
      orange: { name: 'Orange', hex: '#E97132' }
    };

    // Drag & drop handlers pour la pochette
    const handleCoverDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.add('border-lime-400', 'bg-lime-400/10');
    };

    const handleCoverDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.remove('border-lime-400', 'bg-lime-400/10');
    };

    const handleCoverDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.remove('border-lime-400', 'bg-lime-400/10');
      const files = e.dataTransfer.files;
      if (files && files[0] && files[0].type.startsWith('image/')) {
        handleImageUpload('cover_image', files[0]);
      }
    };

    // Drag & drop handlers pour la tracklist
    const handleTracklistDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.add('border-lime-400', 'bg-lime-400/10');
    };

    const handleTracklistDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.remove('border-lime-400', 'bg-lime-400/10');
    };

    const handleTracklistDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.remove('border-lime-400', 'bg-lime-400/10');
      const files = e.dataTransfer.files;
      const fileName = files[0]?.name.toLowerCase() || '';
      if (files && files[0] && (fileName.endsWith('.txt') || fileName.endsWith('.docx'))) {
        handleTracklistUpload(files[0]);
      } else {
        toast.error("Format non supporté. Utilisez .txt ou .docx");
      }
    };

    return (
    <div className="space-y-8">
      {/* Pochette sortie - avec Drag & Drop */}
      <Card 
        className="border-2 border-[#4DBA5F] bg-gradient-to-br from-blue-50 to-indigo-50 transition-all duration-200"
        onDragOver={handleCoverDragOver}
        onDragLeave={handleCoverDragLeave}
        onDrop={handleCoverDrop}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle style={{ color: '#4DBA5F' }} className="text-2xl flex items-center gap-2">
              <Image className="h-6 w-6" />
              Pochette sortie
            </CardTitle>
            <Button
              onClick={() => document.getElementById('cover_image').click()}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              <Upload className="h-4 w-4" />
              Importer
            </Button>
            <Input
              id="cover_image"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleImageUpload('cover_image', file);
              }}
            />
          </div>
        </CardHeader>
        <CardContent>
          {images.cover_image ? (
            <div className="flex flex-col items-center gap-4">
              <img 
                src={URL.createObjectURL(images.cover_image)}
                alt="Pochette"
                className="max-w-xs rounded-lg shadow-lg"
              />
              <p className="text-sm text-green-600 font-medium">✓ {images.cover_image.name}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newImages = { ...images };
                  delete newImages.cover_image;
                  setImages(newImages);
                }}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400 border-2 border-dashed border-gray-300 rounded-lg">
              <div className="text-6xl mb-4">+</div>
              <p className="text-center text-gray-600 font-bold">
                Glisser-déposer un fichier image ici
              </p>
              <p className="text-center text-sm text-gray-500 mt-2 font-bold">
                ou cliquer sur "Importer"
              </p>
              <p className="text-center text-xs text-gray-400 mt-1 font-bold">
                Formats acceptés : JPG, JPEG, PNG
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Liens utiles - Écouter / Clip / Couleur */}
      <Card className="border-2 border-gray-600 bg-gray-800/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-bold text-lime-400">Liens utiles</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-3">
              <Label htmlFor="ecouter_link" className="text-base font-medium text-gray-300">Écouter</Label>
              <Input
                id="ecouter_link"
                value={formData.ecouter_link}
                onChange={(e) => updateFormData('ecouter_link', e.target.value)}
                placeholder="Insérer le lien"
                className="h-12 border-2 border-gray-600 focus:border-lime-400 bg-gray-800 text-white placeholder-gray-500"
              />
            </div>
            {formData.enable_clip && (
              <>
                <div className="space-y-3">
                  <Label htmlFor="clip_link" className="text-base font-medium text-gray-300">Clip</Label>
                  <Input
                    id="clip_link"
                    value={formData.clip_link}
                    onChange={(e) => updateFormData('clip_link', e.target.value)}
                    placeholder="Insérer le lien"
                    className="h-12 border-2 border-gray-600 focus:border-lime-400 bg-gray-800 text-white placeholder-gray-500"
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="clip_button_color" className="text-base font-medium text-gray-300">Choisir la couleur du bouton clip</Label>
                  <Select value={formData.clip_button_color} onValueChange={(value) => updateFormData('clip_button_color', value)}>
                    <SelectTrigger className="h-12 border-2 bg-gray-800 text-white border-gray-600 focus:border-lime-400 shadow-lg">
                      <SelectValue placeholder="Sélectionner une couleur">
                        {formData.clip_button_color && (
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-sm border border-white/30" 
                              style={{ backgroundColor: CLIP_BUTTON_COLORS[formData.clip_button_color]?.hex || '#666' }}
                            />
                            <span>{CLIP_BUTTON_COLORS[formData.clip_button_color]?.name}</span>
                          </div>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 text-white border-gray-600">
                      {Object.entries(CLIP_BUTTON_COLORS).map(([key, { name, hex }]) => (
                    <SelectItem 
                      key={key} 
                      value={key} 
                      className="hover:bg-gray-700 focus:bg-gray-600 cursor-pointer py-2"
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-5 h-5 rounded-sm border border-white/30 flex-shrink-0" 
                          style={{ backgroundColor: hex }}
                        />
                        <span className="font-medium">{name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
          </div>
        </CardContent>
      </Card>

      {/* Tracklist Section - avec Drag & Drop */}
      <Card 
        className="border-2 border-[#4DBA5F] bg-gradient-to-br from-green-50 to-emerald-50 transition-all duration-200"
        onDragOver={handleTracklistDragOver}
        onDragLeave={handleTracklistDragLeave}
        onDrop={handleTracklistDrop}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle style={{ color: '#4DBA5F' }} className="text-2xl flex items-center gap-2">
              <Music className="h-6 w-6" />
              Tracklist
            </CardTitle>
            <div className="flex gap-2">
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                onClick={() => document.getElementById('tracklist_file').click()}
              >
                <Upload className="h-4 w-4" />
                Importer
              </Button>
              {formData.tracks.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                  onClick={() => updateFormData('tracks', [])}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Input
              id="tracklist_file"
              type="file"
              accept=".txt,.docx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleTracklistUpload(file);
              }}
            />
          </div>
        </CardHeader>
        <CardContent>
          {formData.tracks.length > 0 ? (
            <div className="max-h-48 overflow-y-auto space-y-2 p-4 bg-white rounded-lg border">
              {formData.tracks.map((track, index) => (
                <div key={index} className="flex items-center gap-3 text-sm">
                  <span className="bg-green-100 text-green-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </span>
                  <span>{parseMarkdown(track)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
              <Music className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-bold">Glisser-déposer un fichier ici</p>
              <p className="text-sm mt-1 font-bold">ou cliquer sur "Importer"</p>
              <p className="text-xs text-gray-400 mt-1 font-bold">Formats acceptés : TXT, WORD, DOCX</p>
            </div>
          )}
          {/* Aide Markdown pour la tracklist */}
          <div className="bg-green-50 rounded-lg p-3 border border-green-200 mt-4">
            <p className="text-sm text-green-700 font-semibold mb-2">Mise en forme du texte :</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <code className="bg-white px-2 py-1 rounded text-gray-600 border">**texte**</code>
                <span className="text-gray-400">→</span>
                <span className="text-gray-700 font-bold">Gras</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-white px-2 py-1 rounded text-gray-600 border">*texte*</code>
                <span className="text-gray-400">→</span>
                <span className="text-gray-700 italic">Italique</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-white px-2 py-1 rounded text-gray-600 border">_texte_</code>
                <span className="text-gray-400">→</span>
                <span className="text-gray-700 underline">Souligné</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    );
  };

  const renderFormatCard = (index) => (
    <Card key={index}>
      <CardHeader>
        <CardTitle className="text-lg">Format {index}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Type de support</Label>
            <Select onValueChange={(value) => updateFormat(formData.formats[index-1]?.id, 'type', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CD">CD</SelectItem>
                <SelectItem value="LP">LP</SelectItem>
                <SelectItem value="Maxi">Maxi</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Préciser le support (facultatif)</Label>
            <Input
              value={formData.formats[index-1]?.text || ""}
              onChange={(e) => updateFormat(formData.formats[index-1]?.id, 'text', e.target.value)}
              placeholder="Format, options (couleur...), etc."
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date release</Label>
            <Input
              type="date"
              value={formData.formats[index-1]?.release_date || ""}
              onChange={(e) => updateFormat(formData.formats[index-1]?.id, 'release_date', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Prix</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                  {formData.formats[index-1]?.prix || "Choisir un prix"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput placeholder="Rechercher un code prix..." />
                  <CommandList>
                    <CommandEmpty>Aucun résultat</CommandEmpty>
                    <CommandGroup>
                      {kurPrix.map((item, idx) => (
                        <CommandItem
                          key={idx}
                          value={`${item.code} ${item.price}`}
                          onSelect={() => updateFormat(formData.formats[index-1]?.id, 'prix', `${item.code}-${item.price}`)}
                        >
                          {item.code} - {item.price}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Numéro de catalogue</Label>
          <Input
            value={formData.formats[index-1]?.num_catalog || ""}
            onChange={(e) => updateFormat(formData.formats[index-1]?.id, 'num_catalog', e.target.value)}
            placeholder="Ex: 29MU0042CD"
          />
        </div>
        
        {/* Exclus Disquaires Checkbox */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`exclu_disquaires_${index}`}
            checked={formData.formats[index-1]?.exclu_disquaires || false}
            onCheckedChange={(checked) => updateFormat(formData.formats[index-1]?.id, 'exclu_disquaires', checked)}
          />
          <Label htmlFor={`exclu_disquaires_${index}`} className="text-sm font-medium">
            🎯 Exclu Disquaires Indés
          </Label>
        </div>
        
        {/* Code barre upload */}
        <div className="space-y-2">
          <Label>Code-barre</Label>
          <div className="border-2 border-dashed border-[#4DBA5F] rounded-lg p-4 text-center hover:border-green-600 transition-colors">
            <Input
              type="file"
              accept="image/*"
              className="hidden"
              id={`code_barre_${formData.formats[index-1]?.id}`}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleFormatImageUpload(formData.formats[index-1]?.id, file);
              }}
            />
            <Label htmlFor={`code_barre_${formData.formats[index-1]?.id}`} className="cursor-pointer">
              {formData.formats[index-1]?.code_barre_image ? (
                <span className="text-green-600">✓ {formData.formats[index-1]?.code_barre_image.name}</span>
              ) : (
                <span className="text-gray-500">Cliquer pour ajouter un code-barre</span>
              )}
            </Label>
          </div>
        </div>
        
        <Button 
          onClick={() => removeFormat(formData.formats[index-1]?.id)} 
          variant="outline" 
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Supprimer ce support
        </Button>
      </CardContent>
    </Card>
  );

  const renderStep3 = () => (
    <div className="space-y-8">
      {/* Commercial Info */}
      <Card className="border-2 border-[#4DBA5F] bg-gradient-to-br from-green-50 to-emerald-50">
        <CardHeader>
          <CardTitle style={{ color: '#4DBA5F' }} className="text-2xl">Informations commerciales</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label htmlFor="pc_type" className="text-lg font-semibold text-green-700">Type de PC ou Bon</Label>
              <Select value={formData.pc_type} onValueChange={(value) => updateFormData('pc_type', value)}>
                <SelectTrigger className="h-12 border-2 border-green-300 focus:border-green-500">
                  <SelectValue placeholder="Sélectionner le type de PC ou Bon" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PC N°">PC N°</SelectItem>
                  <SelectItem value="PC EXPRESS N°">PC EXPRESS N°</SelectItem>
                  <SelectItem value="BON TECHNIQUE N°">BON TECHNIQUE N°</SelectItem>
                  <SelectItem value="BON DE LIVRAISON N°">BON DE LIVRAISON N°</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label htmlFor="pc_expres" className="text-lg font-semibold text-green-700">Numéro de PC ou Bon</Label>
              <Input
                id="pc_expres"
                value={formData.pc_expres}
                onChange={(e) => updateFormData('pc_expres', e.target.value)}
                placeholder="Écrire un numéro"
                className="h-12 border-2 border-green-300 focus:border-green-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label htmlFor="fin_de_preco" className="text-lg font-semibold text-gray-700">Date de fin de précommande</Label>
              <Input
                id="fin_de_preco"
                type="date"
                value={formData.fin_de_preco}
                onChange={(e) => updateFormData('fin_de_preco', e.target.value)}
                className="h-12 border-2 focus:border-blue-500"
              />
            </div>
            <div className="space-y-3">
              <Label htmlFor="date_de_sortie" className="text-lg font-semibold text-gray-700">Date de sortie</Label>
              <Input
                id="date_de_sortie"
                type="date"
                value={formData.date_de_sortie}
                onChange={(e) => updateFormData('date_de_sortie', e.target.value)}
                className="h-12 border-2 focus:border-blue-500"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dynamic Formats */}
      <Card className="border-2 border-[#4DBA5F] bg-gradient-to-br from-purple-50 to-indigo-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle style={{ color: '#4DBA5F' }} className="text-xl">Supports (max. 3, exceptionnellement 4 avec la personnalisation avancée - cf. Étape 6)</CardTitle>
            <Button onClick={addFormat} className="gap-2 bg-purple-600 hover:bg-purple-700" disabled={formData.formats.length >= 4}>
              <Plus className="h-4 w-4" />
              Ajouter un support
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {formData.formats.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <Plus className="h-8 w-8 text-purple-600" />
              </div>
              <p>Aucun support ajouté</p>
              <p className="text-sm">Cliquer sur "Ajouter un support" pour commencer</p>
            </div>
          ) : (
            formData.formats.map((format) => (
              <Card key={format.id} className="border border-[#4DBA5F] bg-white">
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-purple-700">Support {formData.formats.indexOf(format) + 1}</h4>
                    <Button 
                      onClick={() => removeFormat(format.id)} 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Type de support</Label>
                      <Select value={format.type} onValueChange={(value) => updateFormat(format.id, 'type', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un support" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CD">CD</SelectItem>
                          <SelectItem value="Vinyle">Vinyle</SelectItem>
                          <SelectItem value="Cassette">Cassette</SelectItem>
                          <SelectItem value="DVD">DVD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Préciser le support (facultatif)</Label>
                      <Input
                        value={format.text}
                        onChange={(e) => updateFormat(format.id, 'text', e.target.value)}
                        placeholder="Format, options (couleur...), etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Numéro de catalogue</Label>
                      <Input
                        value={format.num_catalog}
                        onChange={(e) => updateFormat(format.id, 'num_catalog', e.target.value)}
                        placeholder="Remplir"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {formData.sheet_language !== 'EN' && (
                    <div className="space-y-2">
                      <Label>Prix</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                            {format.prix || "Choisir un prix"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Rechercher un code prix..." />
                            <CommandList>
                              <CommandEmpty>Aucun résultat</CommandEmpty>
                              <CommandGroup>
                                {kurPrix.map((item, idx) => (
                                  <CommandItem
                                    key={idx}
                                    value={`${item.code} ${item.price}`}
                                    onSelect={() => updateFormat(format.id, 'prix', `${item.code}-${item.price}`)}
                                  >
                                    {item.code} - {item.price}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    )}
                    <div className="space-y-2">
                      <Label>Date de fin de précommande (facultatif)</Label>
                      <Input
                        type="date"
                        value={format.preorder_end_date}
                        onChange={(e) => updateFormat(format.id, 'preorder_end_date', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de sortie (facultatif)</Label>
                      <Input
                        type="date"
                        value={format.release_date}
                        onChange={(e) => updateFormat(format.id, 'release_date', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  {/* Exclu Disquaires Checkbox */}
                  <div className="flex items-center space-x-2 p-3 bg-yellow-50 rounded-lg border border-[#4DBA5F]">
                    <Checkbox
                      id={`exclu_disquaires_${format.id}`}
                      checked={format.exclu_disquaires || false}
                      onCheckedChange={(checked) => updateFormat(format.id, 'exclu_disquaires', checked)}
                    />
                    <div className="flex flex-col">
                      <Label htmlFor={`exclu_disquaires_${format.id}`} className="text-sm font-medium text-yellow-800">
                        🎯 Exclu Disquaires Indés
                      </Label>
                      <span className="text-xs text-yellow-600">Activer / Désactiver</span>
                    </div>
                  </div>
                  
                  {/* Code barre upload */}
                  <div className="space-y-2">
                    <Label>Code-barre</Label>
                    <div className="border-2 border-dashed border-[#4DBA5F] rounded-lg p-4 text-center hover:border-green-600 transition-colors">
                      <Input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        className="hidden"
                        id={`code_barre_${format.id}`}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleFormatImageUpload(format.id, file);
                        }}
                      />
                      <Label htmlFor={`code_barre_${format.id}`} className="cursor-pointer block">
                        {format.code_barre_image ? (
                          <span className="text-green-600">✓ {format.code_barre_image.name}</span>
                        ) : (
                          <span className="text-gray-500">Cliquer pour ajouter un code-barre</span>
                        )}
                        <span className="block text-xs text-gray-400 mt-1">Formats acceptés : JPG, JPEG, PNG</span>
                      </Label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );

  const resetConcertMode = () => {
    updateFormData('concert_mode', '');
    updateFormData('concert_all_dates', []);
    updateFormData('concert_file_name', '');
    updateFormData('concert_overflow', false);
    updateFormData('concert_free_text', '');
    updateFormData('concerts', []);
  };

  const renderConcertCards = () => {
    if (formData.concerts.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
            <Plus className="h-8 w-8 text-orange-600" />
          </div>
          <p>Aucun concert programmé</p>
          <p className="text-sm">Cliquer sur "Ajouter un concert" pour commencer</p>
        </div>
      );
    }
    return formData.concerts.map((concert) => (
      <Card key={concert.id} className="border border-[#4DBA5F] bg-white">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={concert.date} onChange={(e) => updateConcert(concert.id, 'date', e.target.value)} className="border-2 focus:border-orange-500" />
            </div>
            <div className="space-y-2">
              <Label>Lieu, Ville</Label>
              <Input value={concert.text} onChange={(e) => updateConcert(concert.id, 'text', e.target.value)} placeholder="Préciser" className="border-2 focus:border-orange-500" />
            </div>
            <div className="space-y-2">
              <Label>Pays</Label>
              <Select value={concert.country_code} onValueChange={(value) => updateConcert(concert.id, 'country_code', value)}>
                <SelectTrigger><SelectValue placeholder="Code pays" /></SelectTrigger>
                <SelectContent>
                  {codePays.map((country, idx) => (
                    <SelectItem key={idx} value={country.code}>{country.code} - {country.country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => removeConcert(concert.id)} variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-10">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    ));
  };

  const renderStep4 = () => (
    <div className="space-y-8">
      {/* Choice: Manual or File */}
      {!formData.concert_mode && (
        <Card className="border-2 border-[#4DBA5F] bg-gradient-to-br from-orange-50 to-red-50">
          <CardHeader>
            <CardTitle style={{ color: '#4DBA5F' }} className="text-2xl flex items-center gap-2">
              <Music className="h-6 w-6" />
              Comment souhaitez-vous ajouter vos concerts ?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <button
                data-testid="concert-mode-manual"
                onClick={() => updateFormData('concert_mode', 'manual')}
                className="p-6 rounded-xl border-2 border-gray-200 hover:border-orange-500 hover:bg-orange-50 transition-all text-left group"
              >
                <Plus className="h-10 w-10 text-orange-500 mb-3" />
                <h3 className="text-lg font-bold text-gray-800 mb-1">Ajouter manuellement</h3>
                <p className="text-sm text-gray-500">Saisir les dates de concert une par une</p>
              </button>
              <button
                data-testid="concert-mode-file"
                onClick={() => updateFormData('concert_mode', 'file')}
                className="p-6 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              >
                <FileText className="h-10 w-10 text-blue-500 mb-3" />
                <h3 className="text-lg font-bold text-gray-800 mb-1">Glisser un fichier Word/Excel</h3>
                <p className="text-sm text-gray-500">Importer vos dates depuis un document</p>
              </button>
            </div>
            <button 
              onClick={() => updateFormData('concert_mode', '')}
              className="mt-4 text-sm text-gray-400 hover:text-gray-600 underline"
            >
            </button>
          </CardContent>
        </Card>
      )}

      {/* FILE MODE */}
      {formData.concert_mode === 'file' && (
        <>
          <Card className="border-2 border-[#4DBA5F] bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle style={{ color: '#4DBA5F' }} className="text-2xl flex items-center gap-2">
                  <FileText className="h-6 w-6" />
                  Import de fichier concert
                </CardTitle>
                <Button variant="outline" size="sm" onClick={resetConcertMode}>
                  <RotateCcw className="h-4 w-4 mr-1" /> Changer de mode
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Drop zone */}
              <div
                data-testid="concert-file-dropzone"
                className="border-2 border-dashed border-blue-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-all"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); const file = e.dataTransfer.files[0]; if (file) handleConcertFileUpload(file); }}
                onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = '.docx,.xlsx,.xls,.csv'; input.onchange = (e) => { if (e.target.files[0]) handleConcertFileUpload(e.target.files[0]); }; input.click(); }}
              >
                {formData.concert_file_name ? (
                  <div>
                    <Check className="h-10 w-10 text-green-500 mx-auto mb-2" />
                    <p className="text-green-700 font-semibold">{formData.concert_file_name}</p>
                    <p className="text-sm text-gray-500 mt-1">Cliquez ou glissez un nouveau fichier pour remplacer</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="h-10 w-10 text-blue-400 mx-auto mb-2" />
                    <p className="text-gray-700 font-semibold">Glissez votre fichier ici</p>
                    <p className="text-sm text-gray-500 mt-1">ou cliquez pour sélectionner (.docx, .xlsx, .csv)</p>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Format attendu : une ligne (Word) ou une ligne de tableau (Excel) par concert —{' '}
                <span className="font-semibold">JJ/MM/AAAA - Lieu, Ville, Code pays</span>
              </p>

              {/* Detected dates */}
              {(formData.concert_all_dates || []).length > 0 && (
                <div className="bg-white border rounded-lg p-4" data-testid="concert-detected-list">
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
                    {(formData.concert_all_dates || []).length} date{(formData.concert_all_dates || []).length > 1 ? 's' : ''} détectée{(formData.concert_all_dates || []).length > 1 ? 's' : ''}
                  </p>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {(formData.concert_all_dates || []).map((c, i) => {
                      const line = formatConcertLine(c);
                      const idx = line.indexOf(' - ');
                      return (
                        <p key={i} className="text-sm text-gray-700">
                          <span className="font-bold">{line.slice(0, idx)}</span>{line.slice(idx)}
                        </p>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Overflow : plus de 10 concerts */}
              {formData.concert_overflow && (
                <>
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                    <p className="text-sm font-bold text-amber-800">Plus de 10 concerts détectés ({(formData.concert_all_dates || []).length})</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Résumez la tournée en quelques mots, gardez 1 ou 2 dates sur la fiche, puis publiez la page complète des concerts.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-lg font-semibold text-blue-700">Résumé de la tournée (apparaitra sur la fiche)</Label>
                    <Textarea
                      data-testid="concert-free-text"
                      value={formData.concert_free_text}
                      onChange={(e) => updateFormData('concert_free_text', e.target.value)}
                      placeholder="Ex : Tournée de 25 Zéniths dont"
                      className="min-h-[80px] border-2 border-blue-200 focus:border-blue-500"
                    />
                  </div>

                  <Button
                    data-testid="publish-concert-page-btn"
                    onClick={publishConcertPage}
                    className="gap-2 bg-blue-600 hover:bg-blue-700"
                  >
                    <Globe className="h-4 w-4" />
                    {formData.concert_page_id ? "Mettre à jour la page concerts" : "Publier la page concerts"}
                  </Button>
                  {formData.concert_page_id && (
                    <p className="text-sm text-green-600" data-testid="concert-page-link">
                      Page publiée : <a href={`${window.location.origin}/concerts/${formData.concert_page_id}`} target="_blank" rel="noopener noreferrer" className="underline font-medium">{window.location.origin}/concerts/{formData.concert_page_id}</a>
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Dates affichées sur la fiche */}
          {(formData.concert_all_dates || []).length > 0 && (
            <Card className="border-2 border-[#4DBA5F] bg-gradient-to-br from-orange-50 to-red-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle style={{ color: '#4DBA5F' }} className="text-2xl flex items-center gap-2">
                    <Music className="h-6 w-6" />
                    {formData.concert_overflow ? "Dates affichées sur la fiche (max. 2)" : "Concerts importés (max. 10)"}
                  </CardTitle>
                  <Button
                    onClick={addConcert}
                    className="gap-2 bg-orange-600 hover:bg-orange-700"
                    disabled={formData.concerts.length >= (formData.concert_overflow ? 2 : 10)}
                  >
                    <Plus className="h-4 w-4" />
                    Ajouter un concert
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {renderConcertCards()}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* MANUAL MODE */}
      {formData.concert_mode === 'manual' && (
        <Card className="border-2 border-[#4DBA5F] bg-gradient-to-br from-orange-50 to-red-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle style={{ color: '#4DBA5F' }} className="text-2xl flex items-center gap-2">
                <Music className="h-6 w-6" />
                Concerts (max. 10)
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => updateFormData('concert_mode', '')}>
                  <RotateCcw className="h-4 w-4 mr-1" /> Changer de mode
                </Button>
                <Button onClick={addConcert} className="gap-2 bg-orange-600 hover:bg-orange-700" disabled={formData.concerts.length >= 10}>
                  <Plus className="h-4 w-4" />
                  Ajouter un concert
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {renderConcertCards()}
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderStep5 = () => {
    // Drag & drop handlers for reordering media
    const handleDragStart = (e, index, listType) => {
      e.dataTransfer.setData('dragIndex', index.toString());
      e.dataTransfer.setData('listType', listType);
      e.target.style.opacity = '0.5';
    };

    const handleDragEnd = (e) => {
      e.target.style.opacity = '1';
    };

    const handleDragOver = (e) => {
      e.preventDefault();
    };

    const handleDropMedia = (e, dropIndex) => {
      e.preventDefault();
      const dragIndex = parseInt(e.dataTransfer.getData('dragIndex'));
      const listType = e.dataTransfer.getData('listType');
      
      if (listType !== 'media') return;
      
      const newList = [...formData.selected_media];
      const [removed] = newList.splice(dragIndex, 1);
      newList.splice(dropIndex, 0, removed);
      updateFormData('selected_media', newList);
    };

    const handleDropEtAussi = (e, dropIndex) => {
      e.preventDefault();
      const dragIndex = parseInt(e.dataTransfer.getData('dragIndex'));
      const listType = e.dataTransfer.getData('listType');
      
      if (listType !== 'etaussi') return;
      
      const newList = [...formData.et_aussi_medias];
      const [removed] = newList.splice(dragIndex, 1);
      newList.splice(dropIndex, 0, removed);
      updateFormData('et_aussi_medias', newList);
    };

    const handleDropLogos = (e, dropIndex) => {
      e.preventDefault();
      const dragIndex = parseInt(e.dataTransfer.getData('dragIndex'));
      const listType = e.dataTransfer.getData('listType');
      
      if (listType !== 'logos') return;
      
      const newList = [...formData.selected_logos];
      const [removed] = newList.splice(dragIndex, 1);
      newList.splice(dropIndex, 0, removed);
      updateFormData('selected_logos', newList);
    };

    // Move item up/down helpers
    const moveMediaItem = (index, direction, listType) => {
      let list, updateKey;
      if (listType === 'media') {
        list = [...formData.selected_media];
        updateKey = 'selected_media';
      } else if (listType === 'etaussi') {
        list = [...formData.et_aussi_medias];
        updateKey = 'et_aussi_medias';
      } else if (listType === 'logos') {
        list = [...formData.selected_logos];
        updateKey = 'selected_logos';
      }
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= list.length) return;
      
      [list[index], list[newIndex]] = [list[newIndex], list[index]];
      updateFormData(updateKey, list);
    };

    return (
    <div className="space-y-8">
      {/* Media Selection - Dropdown Style */}
      <Card className="border-2 border-[#4DBA5F] bg-gradient-to-br from-blue-50 to-cyan-50">
        <CardHeader>
          <CardTitle style={{ color: '#4DBA5F' }} className="text-2xl">Médias</CardTitle>
          <CardDescription>Sélectionner les médias souhaités et ajouter une description pour chacun</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Media Combobox with search */}
          <div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between">
                  Sélectionner
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput placeholder="Rechercher un média..." />
                  <CommandList>
                    <CommandEmpty>Aucun résultat</CommandEmpty>
                    <CommandGroup>
                      {mediaList
                        .filter(media => !formData.selected_media.includes(media.name))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((media, index) => (
                        <CommandItem
                          key={index}
                          value={media.name}
                          onSelect={() => toggleMediaSelection(media.name)}
                        >
                          <div className="flex items-center gap-2">
                            {media.filename && (
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/media-icons/${media.filename}`} 
                                alt={media.name}
                                className="w-5 h-5 object-contain"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            )}
                            <span>{media.name}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          
          {/* Selected Media List */}
          {formData.selected_media.length > 0 && (
            <div className="space-y-3">
              {formData.selected_media.map((mediaName, index) => (
                <Card 
                  key={index} 
                  className="p-4 border border-[#4DBA5F]"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col gap-1">
                        <button 
                          onClick={() => moveMediaItem(index, 'up', 'media')}
                          disabled={index === 0}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs"
                        >▲</button>
                        <button 
                          onClick={() => moveMediaItem(index, 'down', 'media')}
                          disabled={index === formData.selected_media.length - 1}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs"
                        >▼</button>
                      </div>
                      <span className="font-medium text-blue-800">{mediaName}</span>
                    </div>
                    <Button
                      onClick={() => toggleMediaSelection(mediaName)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 h-6 w-6 p-0"
                    >
                      ×
                    </Button>
                  </div>
                  <Input
                    placeholder="Type de retombée promo (critique, itw, rotation, émission...), date de parution/diffusion confirmée ou TBC, citation, lien..."
                    value={formData.media_descriptions[mediaName] || ''}
                    onChange={(e) => updateMediaDescription(mediaName, e.target.value)}
                    className="text-sm"
                  />
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Et aussi - Section AVANT Marketing */}
      {formData.enable_et_aussi && (
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
        <CardHeader>
          <CardTitle style={{ color: '#4DBA5F' }} className="text-2xl">Et aussi</CardTitle>
          <CardDescription>Sélectionnez des médias supplémentaires (affichage logo uniquement, sans description)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between">
                  Sélectionner
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput placeholder="Rechercher un média..." />
                  <CommandList>
                    <CommandEmpty>Aucun résultat</CommandEmpty>
                    <CommandGroup>
                      {mediaList
                        .filter(m => !formData.et_aussi_medias.includes(m.name))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((media, idx) => (
                        <CommandItem
                          key={idx}
                          value={media.name}
                          onSelect={() => {
                            updateFormData('et_aussi_medias', [...formData.et_aussi_medias, media.name]);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {media.filename && (
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/media-icons/${media.filename}`} 
                                alt={media.name}
                                className="w-5 h-5 object-contain"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            )}
                            <span>{media.name}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Selected Et Aussi Media List (display as logos) */}
          {formData.et_aussi_medias.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {formData.et_aussi_medias.map((media, index) => (
                  <div 
                    key={index} 
                    className="bg-purple-100 px-3 py-1 rounded-full flex items-center gap-2"
                  >
                    <div className="flex gap-1">
                      <button 
                        onClick={() => moveMediaItem(index, 'up', 'etaussi')}
                        disabled={index === 0}
                        className="text-purple-400 hover:text-purple-600 disabled:opacity-30 text-xs"
                      >◀</button>
                      <button 
                        onClick={() => moveMediaItem(index, 'down', 'etaussi')}
                        disabled={index === formData.et_aussi_medias.length - 1}
                        className="text-purple-400 hover:text-purple-600 disabled:opacity-30 text-xs"
                      >▶</button>
                    </div>
                    <span className="text-sm font-medium">{media}</span>
                    <button
                      onClick={() => updateFormData('et_aussi_medias', formData.et_aussi_medias.filter(m => m !== media))}
                      className="text-red-600 hover:text-red-800"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Marketing et réseaux - Même présentation que Sélection des médias */}
      {formData.enable_marketing && (
        <Card className="border-2 border-[#4DBA5F] bg-gradient-to-br from-indigo-50 to-purple-50">
          <CardHeader>
            <CardTitle style={{ color: '#4DBA5F' }} className="text-2xl">Marketing et réseaux</CardTitle>
            <CardDescription>Sélectionner les médias, réseaux sociaux et plateformes de streaming souhaités et ajouter une description pour chacun</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add Logo Combobox with search */}
            <div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between">
                    Sélectionner
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Rechercher un réseau..." />
                    <CommandList>
                      <CommandEmpty>Aucun résultat</CommandEmpty>
                      <CommandGroup>
                        {logosList
                          .filter(logo => !formData.selected_logos.includes(logo.name))
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((logo, idx) => (
                          <CommandItem
                            key={idx}
                            value={logo.name}
                            onSelect={() => {
                              updateFormData('selected_logos', [...formData.selected_logos, logo.name]);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {logo.filename ? (
                                <img src={`${API}/social-icons/${logo.filename}`} alt={logo.name} className="w-5 h-5 object-contain" />
                              ) : (
                                <Globe className="w-5 h-5 text-gray-400" />
                              )}
                              <span>{logo.name}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Selected Social Media List */}
            {formData.selected_logos.length > 0 && (
            <div className="space-y-3">
              {formData.selected_logos.map((logoName, index) => {
                const logoData = logosList.find(l => l.name === logoName) || {};
                return (
                <Card 
                  key={index} 
                  className="border-green-200 bg-white"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col gap-1 mr-1">
                        <button 
                          onClick={() => moveMediaItem(index, 'up', 'logos')}
                          disabled={index === 0}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs"
                        >▲</button>
                        <button 
                          onClick={() => moveMediaItem(index, 'down', 'logos')}
                          disabled={index === formData.selected_logos.length - 1}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs"
                        >▼</button>
                      </div>
                      <div className="flex-shrink-0">
                        {logoData.filename ? (
                          <img src={`${API}/social-icons/${logoData.filename}`} alt={logoName} className="h-10 w-10 object-contain rounded-lg" />
                        ) : (
                          <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-gray-200">
                            <Globe className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-grow space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-indigo-700">{logoName}</h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateFormData('selected_logos', formData.selected_logos.filter(name => name !== logoName))}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <Textarea
                          placeholder="Type de campagne, budget, dates et durée, nbr d'abonnés..."
                          value={formData.social_media_descriptions[logoName] || ''}
                          onChange={(e) => {
                            const newDescriptions = { ...formData.social_media_descriptions };
                            newDescriptions[logoName] = e.target.value;
                            updateFormData('social_media_descriptions', newDescriptions);
                          }}
                          rows={2}
                          className="text-sm border-indigo-200 focus:border-indigo-400"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
              })}
            </div>
            )}
          </CardContent>
        </Card>
      )}

    </div>
  );
  };

  // Helper pour générer les options de taille de police de 3px à 32px
  const renderFontSizeOptions = (defaultSize = '14px') => {
    const options = [<SelectItem key="default" value="default">Taille par défaut ({defaultSize})</SelectItem>];
    for (let size = 3; size <= 32; size++) {
      options.push(
        <SelectItem key={size} value={`text-[${size}px]`}>
          {size}px
        </SelectItem>
      );
    }
    return options;
  };

  // Helper pour générer les options de police avec police par défaut personnalisable
  const renderFontFamilyOptions = (defaultFont = 'Arial') => {
    const fonts = [
      { value: 'default', label: `Police par défaut (${defaultFont})` },
      { value: 'Arial, sans-serif', label: 'Arial' },
      { value: 'Raleway, sans-serif', label: 'Raleway' },
      { value: 'Helvetica, sans-serif', label: 'Helvetica' },
      { value: 'Times New Roman, serif', label: 'Times New Roman' },
      { value: 'Georgia, serif', label: 'Georgia' },
      { value: 'Courier New, monospace', label: 'Courier New' },
      { value: 'Verdana, sans-serif', label: 'Verdana' },
      { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet MS' },
      { value: 'Comic Sans MS, cursive', label: 'Comic Sans MS' },
      { value: 'Impact, fantasy', label: 'Impact' },
      { value: 'Palatino, serif', label: 'Palatino' }
    ];
    
    return fonts.map(font => (
      <SelectItem key={font.value} value={font.value}>
        {font.label}
      </SelectItem>
    ));
  };

  const renderStep6 = () => {
    // Helper to render a simple size control row
    const SizeControlRow = ({ label, defaultSize, value, onChange }) => (
      <div className="flex items-center justify-between py-3 px-4 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors group">
        <div className="flex items-center gap-3">
          <span className="text-white font-medium">{label}</span>
          <span className="text-gray-500 text-sm">({defaultSize})</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const sizes = ['7px', '8px', '9px', '10px', '11px', '12px', '13px', '14px', '15px', '16px', '18px', '20px', '22px', '24px'];
              const currentIndex = sizes.indexOf(value || defaultSize);
              if (currentIndex > 0) onChange(sizes[currentIndex - 1]);
            }}
            className="w-8 h-8 rounded-full bg-gray-700 hover:bg-[#4DBA5F] text-white flex items-center justify-center transition-colors"
          >
            −
          </button>
          <div className="w-16 text-center">
            <span className="text-[#4DBA5F] font-bold">{value || defaultSize}</span>
          </div>
          <button
            onClick={() => {
              const sizes = ['7px', '8px', '9px', '10px', '11px', '12px', '13px', '14px', '15px', '16px', '18px', '20px', '22px', '24px'];
              const currentIndex = sizes.indexOf(value || defaultSize);
              if (currentIndex < sizes.length - 1) onChange(sizes[currentIndex + 1]);
            }}
            className="w-8 h-8 rounded-full bg-gray-700 hover:bg-[#4DBA5F] text-white flex items-center justify-center transition-colors"
          >
            +
          </button>
          {value && value !== defaultSize && (
            <button
              onClick={() => onChange('')}
              className="ml-2 text-xs text-gray-400 hover:text-red-400 transition-colors"
              title="Réinitialiser"
            >
              ↺
            </button>
          )}
        </div>
      </div>
    );

    // Helper for image size control
    const ImageSizeControl = ({ label, value, onChange, options }) => (
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <label className="text-white font-medium mb-3 block">{label}</label>
        <div className="flex flex-wrap gap-2">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value === 'default' ? '' : opt.value)}
              className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                (value || 'default') === opt.value || (!value && opt.value === 'default')
                  ? 'bg-[#4DBA5F] text-black font-medium'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    );

    // Helper for color picker - dropdown style
    const ColorPicker = ({ label, value, onChange }) => (
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <label className="text-white font-medium mb-3 block">{label}</label>
        <Select 
          value={value || 'default'} 
          onValueChange={(v) => onChange(v === 'default' ? '' : v)}
        >
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
            <SelectValue placeholder="Couleur par défaut" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-600">
            <SelectItem value="default" className="text-white">
              <span className="text-gray-400">Couleur par défaut</span>
            </SelectItem>
            {Object.entries(GENRE_COLORS).map(([genre, color]) => (
              <SelectItem key={genre} value={color} className="text-white">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: color }}></div>
                  <span>{genre}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {value && (
          <div className="flex items-center gap-2 mt-2">
            <div className="w-5 h-5 rounded" style={{ backgroundColor: value }}></div>
            <span className="text-sm text-gray-400">
              {Object.entries(GENRE_COLORS).find(([_, c]) => c === value)?.[0] || 'Personnalisée'}
            </span>
          </div>
        )}
      </div>
    );

    return (
      <div className="space-y-6">
        {/* Header avec bouton reset */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm">Ajuster les tailles et couleurs selon vos besoins</p>
          </div>
          <Button
            onClick={() => {
              // Reset all customization fields
              const resetFields = {
                font_size_genre: '', font_size_artist_title: '', font_size_title: '',
                font_size_buttons: '', font_size_cdlp: '', font_size_argumentaire: '',
                font_size_tracklist: '', font_size_promotion: '', font_size_concert: '', font_size_marketing: '',
                size_exclu_disquaires: '', size_barcode: '', size_label_logo: '',
                date_preco_color: '', date_sortie_color: '', clip_color: '', tracklist_width: ''
              };
              Object.entries(resetFields).forEach(([key, val]) => updateFormData(key, val));
            }}
            variant="outline"
            className="text-gray-400 border-gray-600 hover:bg-gray-800 hover:text-white"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Tout réinitialiser
          </Button>
        </div>

        {/* Section Tailles de police */}
        <Card className="bg-gray-900 border-[#4DBA5F] border-2 overflow-hidden">
          <div className="bg-[#4DBA5F] px-6 py-4">
            <h4 className="text-black font-bold text-lg flex items-center gap-2">
              <Type className="h-5 w-5" />
              Tailles de police
            </h4>
            <p className="text-black/70 text-sm">Police : Arial • Cliquez sur + ou − pour ajuster</p>
          </div>
          <CardContent className="p-4 space-y-2">
            <SizeControlRow 
              label="Genre / Sous-genre" 
              defaultSize="18px" 
              value={formData.font_size_genre}
              onChange={(v) => updateFormData('font_size_genre', v)}
            />
            <SizeControlRow 
              label="Artiste" 
              defaultSize="20px" 
              value={formData.font_size_artist_title}
              onChange={(v) => updateFormData('font_size_artist_title', v)}
            />
            <SizeControlRow 
              label="Titre" 
              defaultSize="15px" 
              value={formData.font_size_title}
              onChange={(v) => updateFormData('font_size_title', v)}
            />
            <SizeControlRow 
              label="Boutons Écouter / Clip" 
              defaultSize="10px" 
              value={formData.font_size_buttons}
              onChange={(v) => updateFormData('font_size_buttons', v)}
            />
            <SizeControlRow 
              label="Supports" 
              defaultSize="10px" 
              value={formData.font_size_cdlp}
              onChange={(v) => updateFormData('font_size_cdlp', v)}
            />
            <SizeControlRow 
              label="Argumentaire" 
              defaultSize="11px" 
              value={formData.font_size_argumentaire}
              onChange={(v) => updateFormData('font_size_argumentaire', v)}
            />
            <SizeControlRow 
              label="Tracklist" 
              defaultSize="14px" 
              value={formData.font_size_tracklist}
              onChange={(v) => updateFormData('font_size_tracklist', v)}
            />
            <SizeControlRow 
              label="Promotion" 
              defaultSize="9px" 
              value={formData.font_size_promotion}
              onChange={(v) => updateFormData('font_size_promotion', v)}
            />
            <SizeControlRow 
              label="En concert" 
              defaultSize="10px" 
              value={formData.font_size_concert}
              onChange={(v) => updateFormData('font_size_concert', v)}
            />
          </CardContent>
        </Card>

        {/* Section Tailles d'images */}
        <Card className="bg-gray-900 border-[#4DBA5F] border-2 overflow-hidden">
          <div className="bg-[#4DBA5F] px-6 py-4">
            <h4 className="text-black font-bold text-lg flex items-center gap-2">
              <Image className="h-5 w-5" />
              Tailles d'images
            </h4>
          </div>
          <CardContent className="p-4 space-y-4">
            <ImageSizeControl
              label="Logo Exclu Disquaires Indés"
              value={formData.size_exclu_disquaires}
              onChange={(v) => updateFormData('size_exclu_disquaires', v)}
              options={[
                { value: 'default', label: 'Auto' },
                { value: '40px', label: '40px' },
                { value: '50px', label: '50px' },
                { value: '60px', label: '60px' },
                { value: '70px', label: '70px' },
                { value: '80px', label: '80px' },
              ]}
            />
            <ImageSizeControl
              label="Code-barres"
              value={formData.size_barcode}
              onChange={(v) => updateFormData('size_barcode', v)}
              options={[
                { value: 'default', label: 'Auto' },
                { value: '50px', label: '50px' },
                { value: '60px', label: '60px' },
                { value: '70px', label: '70px' },
                { value: '80px', label: '80px' },
                { value: '90px', label: '90px' },
              ]}
            />
            <ImageSizeControl
              label="Logo label"
              value={formData.size_label_logo}
              onChange={(v) => updateFormData('size_label_logo', v)}
              options={[
                { value: 'default', label: 'Auto' },
                { value: '60px', label: '60px' },
                { value: '80px', label: '80px' },
                { value: '100px', label: '100px' },
                { value: '132px', label: '132px (défaut)' },
                { value: '160px', label: '160px' },
                { value: '190px', label: '190px' },
              ]}
            />
          </CardContent>
        </Card>

        {/* Section Couleurs */}
        <Card className="bg-gray-900 border-[#4DBA5F] border-2 overflow-hidden">
          <div className="bg-[#4DBA5F] px-6 py-4">
            <h4 className="text-black font-bold text-lg flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Couleurs
            </h4>
          </div>
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <ColorPicker
              label="Fin de précommande"
              value={formData.date_preco_color}
              onChange={(v) => updateFormData('date_preco_color', v)}
            />
            <ColorPicker
              label="Sortie"
              value={formData.date_sortie_color}
              onChange={(v) => updateFormData('date_sortie_color', v)}
            />
            <ColorPicker
              label="Clip officiel"
              value={formData.clip_color}
              onChange={(v) => updateFormData('clip_color', v)}
            />
          </CardContent>
        </Card>

        {/* Section Colonnes */}
        <Card className="bg-gray-900 border-[#4DBA5F] border-2 overflow-hidden">
          <div className="bg-[#4DBA5F] px-6 py-4">
            <h4 className="text-black font-bold text-lg flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Largeur des colonnes
            </h4>
          </div>
          <CardContent className="p-4">
            <div className="p-4 bg-gray-800/50 rounded-lg">
              <label className="text-white font-medium mb-3 block">Tracklist / Promotion</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'default', label: '30% / 70%', desc: 'Par défaut' },
                  { value: '35', label: '35% / 65%', desc: '' },
                  { value: '40', label: '40% / 60%', desc: '' },
                  { value: '45', label: '45% / 55%', desc: '' },
                  { value: '50', label: '50% / 50%', desc: 'Équilibré' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => updateFormData('tracklist_width', opt.value === 'default' ? '' : opt.value)}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      (formData.tracklist_width || 'default') === opt.value || (!formData.tracklist_width && opt.value === 'default')
                        ? 'bg-[#4DBA5F] text-black font-medium'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {opt.label}
                    {opt.desc && <span className="block text-xs opacity-70">{opt.desc}</span>}
                  </button>
                ))}
              </div>
              {formData.tracklist_width && (
                <div className="mt-4 flex items-center gap-2">
                  <div className="flex-1 h-3 bg-gray-700 rounded-full overflow-hidden flex">
                    <div 
                      className="bg-blue-500 h-full transition-all" 
                      style={{ width: `${formData.tracklist_width}%` }}
                    />
                    <div 
                      className="bg-[#4DBA5F] h-full transition-all" 
                      style={{ width: `${100 - parseInt(formData.tracklist_width)}%` }}
                    />
                  </div>
                  <span className="text-gray-400 text-sm w-24 text-right">
                    {formData.tracklist_width}% / {100 - parseInt(formData.tracklist_width)}%
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return "Informations artistiques";
      case 2: return "Médias & contenu";
      case 3: return "Supports & commercialisation";
      case 4: return "En concert";
      case 5: return "Promotion";
      case 6: return "Personnalisation avancée (facultatif)";
      default: return "";
    }
  };

  const getTotalSteps = () => {
    return 6;
  };

  const getDisplayStep = () => {
    return currentStep;
  };

  const getStepIcon = () => {
    switch (currentStep) {
      case 1: return <Sparkles className="h-5 w-5" />;
      case 2: return <Music className="h-5 w-5" />;
      case 3: return <Image className="h-5 w-5" />;
      case 4: return <Star className="h-5 w-5" />;
      case 5: return <Globe className="h-5 w-5" />;
      case 6: return <Settings className="h-5 w-5" />;
      default: return <Sparkles className="h-5 w-5" />;
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.artist && formData.album && formData.genre;
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
        return true;
      default:
        return false;
    }
  };

  // Show web generator if requested
  if (showWebGenerator) {
    return (
      <>
        <WebPPTGenerator
          formData={formData}
          images={images}
          kurPrix={kurPrix}
          logosList={logosList}
          labelLogos={labelLogos}
          genresList={genresList}
          onSave={openSaveDialog}
          onBack={() => setShowWebGenerator(false)}
        />
        {renderSaveDialog()}
        <Toaster position="bottom-right" className="no-print" />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Header with user info */}
          <div className="text-center mb-12">
            <div className="flex justify-between items-center mb-4">
              <div className="text-left">
                <p className="text-lime-400 text-sm">Connecté: <span className="font-bold">{user?.username}</span></p>
                <p className="text-gray-500 text-xs">{user?.role === 'admin' ? '👑 Administrateur' : '👤 Utilisateur'}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => navigate('/sheets')} data-testid="saved-sheets-btn" className="bg-blue-500 text-white hover:bg-blue-600">
                  <FolderOpen className="mr-2 h-4 w-4" />
                  Fiches enregistrées
                </Button>
                {isAdmin && (
                  <Button onClick={() => navigate('/admin')} className="bg-lime-400 text-black hover:bg-lime-500">
                    <Settings className="mr-2 h-4 w-4" />
                    Administration
                  </Button>
                )}
                <Button onClick={logout} variant="destructive">
                  Déconnexion
                </Button>
              </div>
            </div>
            <h1 className="text-6xl font-bold mb-4 drop-shadow-lg" style={{ fontFamily: 'Raleway, sans-serif', color: '#4DBA5F' }}>
              Kuroneko
            </h1>
            <p className="text-xl font-semibold" style={{ fontFamily: 'Raleway, sans-serif', color: '#4DBA5F' }}>
              Générateur de fiches promotionnelles
            </p>
          </div>
          
          {/* Auto-fill button for testing */}
          <div className="mb-6 flex justify-center">
            <Button 
              onClick={autoFillForm}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Remplir automatiquement (Mode Test)
            </Button>
          </div>

          {/* Progress bar */}
          <div className="mb-12">
            <div className="flex justify-between mb-4">
              {[1, 2, 3, 4, 5, 6].map((step) => (
                <div key={step} className="flex flex-col items-center cursor-pointer" onClick={() => setCurrentStep(step)}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 shadow-lg ${
                    step === currentStep 
                      ? 'bg-gradient-to-r from-lime-400 to-green-400 text-black shadow-green-400/50 scale-110' 
                      : step < currentStep 
                        ? 'bg-green-500 text-white shadow-green-500/30 hover:scale-105'
                        : 'bg-gray-800 text-gray-500 border-2 border-gray-700 hover:border-gray-500'
                  }`}>
                    {step < currentStep ? '✓' : step}
                  </div>
                  <span className={`text-xs mt-2 text-center font-semibold ${
                    step === currentStep ? 'text-lime-400 drop-shadow-sm' : 'text-gray-500'
                  }`}>
                    Étape {step}
                  </span>
                </div>
              ))}
            </div>
            <Progress value={(getDisplayStep() / getTotalSteps()) * 100} className="h-2 mb-4" />
            <div className="flex justify-between text-sm text-gray-600">
              <span>Étape {getDisplayStep()} sur {getTotalSteps()}</span>
              <span>{Math.round((getDisplayStep() / getTotalSteps()) * 100)}% terminé</span>
            </div>
          </div>
          
          {/* Main Card */}
          <Card className="mb-8 shadow-2xl border-0 bg-gray-900/95 backdrop-blur-sm border border-green-500/20">
            <CardHeader style={{ backgroundColor: '#4DBA5F' }} className="text-black rounded-t-lg shadow-lg">
              <CardTitle className="text-3xl flex items-center gap-3 font-bold">
                {getStepIcon()}
                {getStepTitle()}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8 bg-gray-900 text-white border-x border-green-500/10">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
              {currentStep === 5 && renderStep5()}
              {currentStep === 6 && renderStep6()}
            </CardContent>
          </Card>
          
          {/* Navigation buttons */}
          <div className="flex justify-between">
            <Button
              onClick={prevStep}
              disabled={currentStep === 1}
              variant="outline"
              className="gap-2 h-12 px-8 border-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Précédent
            </Button>
            
            {currentStep < 6 ? (
              <Button
                onClick={nextStep}
                disabled={!isStepValid()}
                className="gap-2 h-12 px-8 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
              >
                Suivant
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={generateWebVersion}
                disabled={!isStepValid()}
                className="gap-2 h-12 px-8 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-lg"
              >
                <Globe className="h-5 w-5" />
                Générer Fiche Promotionnelle
              </Button>
            )}
          </div>

          {renderSaveDialog()}
        </div>
      </div>
      <Toaster position="top-right" className="no-print" />
    </div>
  );
}

export default FormGenerator;